import { useCallback, useState } from 'react';
import styles from './ShopifyUrlDialog.module.css';
import getCMResponse from '../../../utils/api_manager';
import { saveForShopifyRedirect } from '../../../utils/shopifyGrantFlowProject';

const Icon = ({ name, size = 18, color = 'inherit', style = {} }) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, color, lineHeight: 1, ...style }}
  >
    {name}
  </span>
);

// Multi-Platform Business Support TRD, Bounded Unit B, Requirement B3 /
// Decision D6 -- this is the relocated (not reimplemented) version of the
// exact store-URL format check that used to live in the pre-TRD Step 1 URL
// field (Onboarding/EnterOrganizationDetails.jsx, now SetUpProject.jsx),
// which itself round-tripped the format check through cq-api's
// ShopifyService.validateShopDomain (invoked from POST /project's now-legacy
// shopify_store path). That server-side format check is a pure regex with no
// external dependency, so it is reproduced verbatim here for the same
// accept/reject behavior (AC-B3.4) -- see
// cq-api/services/datasources/shopify/shopify.service.js's
// validateShopDomain for the canonical copy this must stay in sync with.
//
// NOTE (flagged as a follow-up, not fixed by this unit): the deeper
// "does this shop actually exist" check the old flow additionally performed
// via ProjectService._verifyShopifyStoreExists (a real network call to
// Shopify, made at project-creation time) is NOT available as a standalone
// endpoint against an already-created project -- GET
// /datasource/shopify/request-permissions never performed that check either,
// it only builds the OAuth redirect URL. So this dialog's inline error only
// catches malformed input; a well-formed but non-existent store name will
// only fail once the user reaches Shopify's own OAuth screen. Closing that
// gap requires a new cq-api validation endpoint, which is out of this
// bounded unit's (cq-app) scope -- see this agent's completion summary.
export function isValidShopDomainFormat(value) {
  if (!value || typeof value !== 'string') return false;
  const cleanDomain = value.trim().replace(/\.myshopify\.com$/i, '');
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,58}[a-zA-Z0-9]$/;
  return domainRegex.test(cleanDomain);
}

/* ─── ShopifyUrlDialog ───────────────────────────────────────────────
 * Onboarding Step 2's Shopify-tile-only dialog (per the approved design's
 * "Connect Shopify" / error-state frames). Validation runs on CTA click only
 * (PRD §8 Q16) -- never on blur/keystroke. Valid -> calls the existing
 * GET /datasource/shopify/request-permissions and full-page-redirects to
 * Shopify OAuth (unchanged destination, AC-B3.3). Invalid -> inline error in
 * this same dialog, no navigation, field stays editable for a retry
 * (AC-B3.2).
 */
const ShopifyUrlDialog = ({ project, onClose }) => {
  const [shopDomain, setShopDomain] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleContinue = useCallback(() => {
    const trimmed = shopDomain.trim();

    if (!trimmed) {
      setError('Shopify store URL is required.');
      return;
    }

    if (!isValidShopDomainFormat(trimmed)) {
      setError("We couldn't find a Shopify store at that address. Check the spelling — only letters, numbers, and hyphens are allowed.");
      return;
    }

    setError(null);
    setSubmitting(true);
    const cleanDomain = trimmed.replace(/\.myshopify\.com$/i, '');

    getCMResponse('/datasource/shopify/request-permissions', 'GET', {
      shop_domain: cleanDomain,
      project_id: project?.id,
    })
      .then((response) => {
        // connect-cta-new-tab-project-leak-trd.md Section 2.3/2.4, Section 3
        // R7: the one remaining case that still needs a storage-backed
        // signal -- the same-tab full-page Shopify OAuth redirect below,
        // whose return leg cannot always carry a project ID in its path
        // (cq-api's cookie-missing callback branch). Saved immediately
        // before the redirect away, consumed on return by
        // LegacyProjectRedirect's /grant-access alias.
        saveForShopifyRedirect(project);
        window.location.href = response.redirect_url;
      })
      .catch((err) => {
        console.error('Error initiating Shopify OAuth:', err);
        setSubmitting(false);
        setError('Failed to start Shopify authorization. Please try again.');
      });
  }, [shopDomain, project]);

  return (
    <div className={styles.overlay} onClick={submitting ? undefined : onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>
              <Icon name="storefront" size={18} color="var(--color-primary)" />
            </span>
            <h3 className={styles.title}>Connect Shopify</h3>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            <Icon name="close" size={18} color="var(--color-on-surface-muted)" />
          </button>
        </div>

        <p className={styles.body}>
          We ask for your store's URL to start Shopify's secure authorization
          flow and to confirm it points to a real, reachable store before you
          leave Cliqmetrics.
        </p>

        <label className={styles.fieldLabel} htmlFor="shopify-url-dialog-input">
          Shopify store URL
        </label>
        <div className={`${styles.inputWrapper} ${error ? styles.inputWrapperError : ''}`}>
          <input
            id="shopify-url-dialog-input"
            type="text"
            className={styles.input}
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            placeholder="mystore"
            disabled={submitting}
            autoComplete="off"
            autoFocus
          />
          <span className={styles.inputSuffix}>.myshopify.com</span>
        </div>
        {error && (
          <p className={styles.errorText} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className={styles.continueBtn} onClick={handleContinue} disabled={submitting}>
            {submitting ? 'Connecting…' : (
              <>
                Continue to Shopify
                <Icon name="arrow_forward" size={15} color="#fff" style={{ marginLeft: 6 }} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopifyUrlDialog;
